import React, { useState, useEffect } from "react";
import axios from "axios";
import '../../../styles/modal.css';
// import { toast } from 'react-toastify'; // Commented out - using react-toast now
import { toast } from "react-toast";
import { useLocation, useNavigate } from 'react-router';
import axiosInstance from "../../../api/axiosInstance";
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

// SVG Icons
const JiraIcon = ({ isSelected }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill={isSelected ? "#0052CC" : "#9CA3AF"}
  >
    <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005z" />
    <path d="M5.01 11.513H16.58a5.215 5.215 0 0 0-5.232-5.215H9.22V4.243A5.216 5.216 0 0 0 4.004 0v11.513a1.005 1.005 0 0 0 1.005 0z" fill={isSelected ? "#2684FF" : "#D1D5DB"} />
  </svg>
);

const WorkXiqIcon = ({ isSelected }) => (
  <img
    src="/workxiq-logo.svg"
    alt="WorkXiq"
    width="24"
    height="24"
    style={{ objectFit: "contain", opacity: isSelected ? 1 : 0.5 }}
  />
);

const SourceSelector = ({ selectedSource, onSourceChange, isLoading }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-600 mb-3">
      Select Source
    </label>

    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg w-fit">
      {[
        { id: "jira", label: "Jira", icon: JiraIcon },
        { id: "projectpulse", label: "WorkXiq", icon: WorkXiqIcon },
      ].map((source) => {
        const IconComponent = source.icon;
        const isSelected = selectedSource === source.id;

        return (
          <button
            key={source.id}
            onClick={() => onSourceChange(source.id)}
            disabled={isLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all
              ${
                isSelected
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-800"
              }
              ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            <IconComponent isSelected={isSelected} />

            <span>{source.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const JiraImportModal = ({ isOpen, onClose, onImport }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedSource, setSelectedSource] = useState("jira");
  const [importedProjects, setImportedProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation(); // Using window.location to avoid issues with useLocation in modals

  useEffect(() => {
    if (isOpen) {
      setSelectedSource("");
      setImportedProjects([]);
      setSelectedProjects([]);
      setIsLoading(false);
      setIsSyncing(false);
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen]);

  const fetchSourceProjects = async (source) => {
    setIsLoading(true);
    setError(null);

    try {
      const authDataString = localStorage.getItem("user");
      if (!authDataString) {
        throw new Error("User not authenticated - no auth data found");
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error("Access token not found in auth data");
      }

      const sourceParam = source === "jira" ? 1 : 2; 
      const response = await axiosInstance.get(
        `/jira_data/get_projects?source=${sourceParam}`
      );

      // Handle different response structures based on source
      let projects = [];
      if (source === "jira") {
        projects = response.projects || [];
      } else if (source === "projectpulse") {
        projects = response.projects?.results || response.projects || [];
      }

      setImportedProjects(projects);
    } catch (err) {
      const initialSeconds = 3;
      let baseMessage;

      if (err.response?.status === 500) {
        setError("Network Error");
        return;
      } else {
        baseMessage =
          err.response?.data?.detail ||
          err.message ||
          `Failed to fetch ${source === "jira" ? "Jira" : "WorkXiq"} projects`;
      }

      let secondsLeft = initialSeconds;
      setError(
        `${baseMessage}\nYou are going to redirect after ${secondsLeft} seconds on configuration page`
      );

      const countdownInterval = setInterval(() => {
        secondsLeft -= 1;
        if (secondsLeft > 0) {
          setError(
            `${baseMessage}\nYou are going to redirect after ${secondsLeft} seconds on configuration page`
          );
        } else {
          clearInterval(countdownInterval);
          navigate("/dashboard/settings", {
            state: { autoOpenIntegration: true },
          });
        }
      }, 1000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSourceChange = (source) => {
    setSelectedSource(source);
    setSelectedProjects([]);
    setImportedProjects([]);
    setError(null);
    setSuccessMessage(null);
    fetchSourceProjects(source);
  };

  useEffect(() => {
    if (isOpen) {
      // fetchSourceProjects(selectedSource);
    }
  }, [isOpen]);

  const syncImportedProjects = async () => {
    if (selectedProjects.length === 0) {
      setError("Please select at least one project to import");
      return;
    }

    setIsSyncing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const authDataString = localStorage.getItem("user");
      if (!authDataString) {
        throw new Error("User not authenticated - no auth data found");
      }

      const authData = JSON.parse(authDataString);
      const accessToken = authData.accessToken;
      if (!accessToken) {
        throw new Error("Access token not found in auth data");
      }
      const workspaceId = parseInt(location.pathname.split('/')[1].split('_')[1], 10) || null;

      const sourceParam = selectedSource === "jira" ? 1 : 2;

      const response = await axios.post(
        `${API_BASE_URL}/jira_data/sync?source=${sourceParam}&workspace_id=${workspaceId}`,
        {
          selected_project_ids: selectedProjects.map((project) => project.id),
        },
        {
          headers: {
            token: `${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Using react-toast library for success notification
      toast.success(response.data?.message || "Projects imported successfully");

      // Call the onImport callback to refresh the project list
      if (onImport) {
        onImport(selectedProjects);
      }

      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to import projects"
      );
      console.error("Error syncing projects:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const toggleProjectSelection = (project) => {
    setSelectedProjects((prev) =>
      prev.some((p) => p.id === project.id)
        ? prev.filter((p) => p.id !== project.id)
        : [...prev, project]
    );
    // Clear any previous error when user selects a project
    if (error === "Please select at least one project to import") {
      setError(null);
    }
  };

  if (!isOpen) return null;

  const sourceLabels = {
    jira: "Jira",
    projectpulse: "WorkXiq"
  };

  return (
 <div data-rt-modal-overlay className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
  <div className="w-full max-w-3xl h-[75vh] bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden">

    {/* Header */}
    <div className="rt-modal__header">
      <div>
        <h2>Import Projects</h2>
        <p>From {sourceLabels[selectedSource]}</p>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="rt-modal__close"
        aria-label="Close"
      >
        ✕
      </button>
    </div>

    {/* Step Indicator */}
    <div className="px-6 pt-4 text-xs text-gray-500 flex items-center gap-2">
      <span className="text-blue-600 font-medium">1. Source</span>
      <span>→</span>
      <span className="text-blue-600 font-medium">2. Select Projects</span>
      <span>→</span>
      <span>3. Import</span>
    </div>

    {/* Content */}
    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

      {/* Source Selector */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <SourceSelector
          selectedSource={selectedSource}
          onSourceChange={handleSourceChange}
          isLoading={isLoading}
        />
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="mt-3 text-sm text-gray-500">
            Loading {sourceLabels[selectedSource]} projects...
          </p>
        </div>
      ) : error ? (

        /* Error */
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <span className="text-red-500">⚠</span>
          <p className="text-sm text-red-700 whitespace-pre-line">
            {error}
          </p>
        </div>

      ) : importedProjects.length > 0 ? (

        <>
          {/* Info */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Select projects to import
            </p>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              {selectedProjects.length} selected
            </span>
          </div>

          {/* Project List */}
          <div className="grid gap-2 max-h-[320px] overflow-y-auto pr-1 border border-gray-200 rounded-lg">
            {importedProjects.map((project) => {
              const isSelected = selectedProjects.some(
                (p) => p.id === project.id
              );

              return (
                <div
                  key={project.id}
                  onClick={() => toggleProjectSelection(project)}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition border-b last:border-b-0
                    ${
                      isSelected
                        ? "bg-blue-50 border-blue-300"
                        : "hover:bg-gray-50"
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleProjectSelection(project)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4"
                  />

                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">
                      {project.name}
                    </h4>
                    {project.key && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {project.key}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>

      ) : (

        /* Empty */
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <span className="text-4xl mb-2">📂</span>
          <p>No projects found</p>
        </div>

      )}
    </div>

    {/* Footer */}
    <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
      
      <button
        onClick={onClose}
        disabled={isLoading || isSyncing}
        className="px-4 py-2 text-sm rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
      >
        Cancel
      </button>

      <button
        onClick={syncImportedProjects}
        disabled={isLoading || isSyncing || selectedProjects.length === 0}
        className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
      >
        {isSyncing && (
          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        Import ({selectedProjects.length})
      </button>

    </div>
  </div>
</div>
  );
};

export default JiraImportModal;
